/**
 * Agent Hub — Suggestions Tab
 *
 * Shows bot improvement suggestions generated by auto-learning.
 * Follows KnowledgeTab patterns: stat cards, filters, table, pagination, empty state.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Lightbulb,
  RefreshCw,
  Loader2,
  AlertCircle,
  MoreHorizontal,
  CheckCircle,
  Eye,
  XCircle,
  ChevronDown,
  ChevronRight,
  BookOpen,
  AlertTriangle,
  LinkIcon,
  TrendingUp,
  HelpCircle,
} from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Badge } from '../../../components/ui/Badge'
import { Card, CardContent } from '../../../components/ui/Card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/ShadcnDropdownMenu'
import { useToast } from '../../../hooks/useToast'
import type { Chatbot } from '../../../types'
import { cn } from '../../../utils/cn'
import { api } from '../../../services/api'

// ── Interfaces ────────────────────────────
interface Suggestion {
  id: string
  suggestion_type: string
  title: string
  detail: string | null
  source_quote: string | null
  status: string
  conversation_id: string | null
  created_at: string | null
  resolved_at: string | null
}

type StatusFilter = 'all' | 'open' | 'acknowledged' | 'resolved' | 'dismissed'
type TypeFilter = 'all' | 'missing_knowledge' | 'wrong_answer' | 'broken_link' | 'frequent_topic' | 'confused_visitor'

// ── Mappings ──────────────────────────────
const TYPE_CONFIG: Record<string, { label: string; variant: 'warning' | 'error' | 'destructive' | 'info'; icon: React.ElementType }> = {
  missing_knowledge: { label: 'Missing Knowledge', variant: 'warning', icon: BookOpen },
  wrong_answer: { label: 'Wrong Answer', variant: 'error', icon: AlertTriangle },
  broken_link: { label: 'Broken Link', variant: 'destructive', icon: LinkIcon },
  frequent_topic: { label: 'Frequent Topic', variant: 'info', icon: TrendingUp },
  confused_visitor: { label: 'Confused User', variant: 'warning', icon: HelpCircle },
}

const STATUS_CONFIG: Record<string, { label: string; variant: 'info' | 'warning' | 'success' | 'outline' }> = {
  open: { label: 'Open', variant: 'info' },
  acknowledged: { label: 'Acknowledged', variant: 'warning' },
  resolved: { label: 'Resolved', variant: 'success' },
  dismissed: { label: 'Dismissed', variant: 'outline' },
}

// ── Component ─────────────────────────────
interface SuggestionsTabProps {
  chatbot: Chatbot
}

export const SuggestionsTab: React.FC<SuggestionsTabProps> = ({ chatbot }) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const itemsPerPage = 10

  const { toast } = useToast()

  // ── Data loading ──
  const loadSuggestions = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const params = new URLSearchParams()
      if (statusFilter !== 'all') params.set('status', statusFilter)
      if (typeFilter !== 'all') params.set('type', typeFilter)
      params.set('limit', String(itemsPerPage))
      params.set('offset', String((currentPage - 1) * itemsPerPage))

      const response = await api.get(`/bot-suggestions/chatbot/${chatbot.id}?${params.toString()}`)
      setSuggestions(response.data.suggestions || [])
      setTotal(response.data.total || 0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load suggestions')
    } finally {
      setIsLoading(false)
    }
  }, [chatbot.id, statusFilter, typeFilter, currentPage])

  useEffect(() => { loadSuggestions() }, [loadSuggestions])
  useEffect(() => { setCurrentPage(1) }, [statusFilter, typeFilter])

  // ── Status update ──
  const updateStatus = async (suggestionId: string, newStatus: string) => {
    try {
      await api.patch(`/bot-suggestions/${suggestionId}?status=${newStatus}`)
      setSuggestions(prev =>
        prev.map(s =>
          s.id === suggestionId
            ? { ...s, status: newStatus, resolved_at: newStatus === 'resolved' ? new Date().toISOString() : s.resolved_at }
            : s,
        ),
      )
      toast({ title: 'Status Updated', description: `Suggestion marked as ${newStatus}`, variant: 'success' })
    } catch (err) {
      toast({ title: 'Update Failed', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' })
    }
  }

  // ── Analytics (computed from total + filtered data) ──
  // We load all for stat cards with a separate call when filters are active
  const [allSuggestions, setAllSuggestions] = useState<Suggestion[]>([])
  const loadAllForStats = useCallback(async () => {
    try {
      const response = await api.get(`/bot-suggestions/chatbot/${chatbot.id}?limit=200&offset=0`)
      setAllSuggestions(response.data.suggestions || [])
    } catch {
      // Stats will show 0 — non-critical
    }
  }, [chatbot.id])

  useEffect(() => { loadAllForStats() }, [loadAllForStats])

  const analytics = useMemo(() => {
    const all = allSuggestions
    return {
      open: all.filter(s => s.status === 'open').length,
      acknowledged: all.filter(s => s.status === 'acknowledged').length,
      resolved: all.filter(s => s.status === 'resolved').length,
      dismissed: all.filter(s => s.status === 'dismissed').length,
    }
  }, [allSuggestions])

  // ── Pagination ──
  const totalPages = Math.ceil(total / itemsPerPage)

  // ── Stat cards config ──
  const statCards = [
    { label: 'Open', value: analytics.open, icon: AlertCircle, iconClass: 'text-blue-600 bg-blue-500/10 dark:text-blue-400' },
    { label: 'Acknowledged', value: analytics.acknowledged, icon: Eye, iconClass: 'text-amber-600 bg-amber-500/10 dark:text-amber-400' },
    { label: 'Resolved', value: analytics.resolved, icon: CheckCircle, iconClass: 'text-green-600 bg-green-500/10 dark:text-green-400' },
    { label: 'Dismissed', value: analytics.dismissed, icon: XCircle, iconClass: 'text-muted-foreground bg-muted' },
  ]

  return (
    <div className="space-y-6">
      {/* ── Stat cards ── */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map(stat => (
          <Card key={stat.label}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-muted-foreground">{stat.label}</p>
                <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', stat.iconClass)}>
                  <stat.icon className="h-4 w-4" />
                </div>
              </div>
              <p className="text-3xl font-bold tracking-tight text-foreground">{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {/* Status filter */}
          <div className="flex items-center gap-0.5 rounded-lg bg-muted p-1">
            {([
              { value: 'all', label: 'All' },
              { value: 'open', label: 'Open' },
              { value: 'acknowledged', label: 'Acknowledged' },
              { value: 'resolved', label: 'Resolved' },
              { value: 'dismissed', label: 'Dismissed' },
            ] as const).map(opt => (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-all',
                  statusFilter === opt.value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Type filter */}
          <div className="flex items-center gap-0.5 rounded-lg bg-muted p-1">
            {([
              { value: 'all', label: 'All Types' },
              { value: 'missing_knowledge', label: 'Missing' },
              { value: 'wrong_answer', label: 'Wrong' },
              { value: 'frequent_topic', label: 'Frequent' },
            ] as const).map(opt => (
              <button
                key={opt.value}
                onClick={() => setTypeFilter(opt.value)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm font-medium transition-all',
                  typeFilter === opt.value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <Button variant="outline" size="icon" onClick={() => { loadSuggestions(); loadAllForStats() }} disabled={isLoading}>
          <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
        </Button>
      </div>

      {/* ── Suggestions list ── */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading suggestions...</p>
        </div>
      ) : error ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 gap-2">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <p className="text-sm font-medium text-foreground">Failed to load</p>
            <p className="text-xs text-muted-foreground">{error}</p>
            <Button variant="link" size="sm" onClick={() => loadSuggestions()} className="mt-1">
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : suggestions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
              <Lightbulb className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-foreground">
                {statusFilter !== 'all' || typeFilter !== 'all' ? 'No matching suggestions' : 'No suggestions yet'}
              </p>
              <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                {statusFilter !== 'all' || typeFilter !== 'all'
                  ? 'Try adjusting your filters.'
                  : 'Enable auto-learning in Settings to let the bot analyze conversations and generate improvement suggestions.'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              {total} suggestion{total !== 1 ? 's' : ''}
            </span>
            {totalPages > 1 && (
              <span className="text-xs text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
            )}
          </div>

          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50%]">Suggestion</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {suggestions.map(s => {
                  const typeConf = TYPE_CONFIG[s.suggestion_type] || { label: s.suggestion_type, variant: 'outline' as const, icon: Lightbulb }
                  const statusConf = STATUS_CONFIG[s.status] || { label: s.status, variant: 'outline' as const }
                  const TypeIcon = typeConf.icon
                  const isExpanded = expandedId === s.id

                  return (
                    <React.Fragment key={s.id}>
                      <TableRow
                        className="group cursor-pointer"
                        onClick={() => setExpandedId(isExpanded ? null : s.id)}
                      >
                        {/* Title + expand indicator */}
                        <TableCell>
                          <div className="flex items-start gap-2">
                            {isExpanded
                              ? <ChevronDown className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                              : <ChevronRight className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                            }
                            <span className="text-sm font-medium text-foreground">{s.title}</span>
                          </div>
                        </TableCell>

                        {/* Type badge */}
                        <TableCell>
                          <Badge variant={typeConf.variant} className="gap-1">
                            <TypeIcon className="h-3 w-3" />
                            {typeConf.label}
                          </Badge>
                        </TableCell>

                        {/* Status badge */}
                        <TableCell>
                          <Badge variant={statusConf.variant}>{statusConf.label}</Badge>
                        </TableCell>

                        {/* Date */}
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
                        </TableCell>

                        {/* Actions */}
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={e => e.stopPropagation()}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {s.status !== 'acknowledged' && (
                                <DropdownMenuItem onClick={() => updateStatus(s.id, 'acknowledged')}>
                                  <Eye className="h-4 w-4" />
                                  <span>Acknowledge</span>
                                </DropdownMenuItem>
                              )}
                              {s.status !== 'resolved' && (
                                <DropdownMenuItem onClick={() => updateStatus(s.id, 'resolved')}>
                                  <CheckCircle className="h-4 w-4" />
                                  <span>Mark Resolved</span>
                                </DropdownMenuItem>
                              )}
                              {s.status !== 'dismissed' && (
                                <DropdownMenuItem onClick={() => updateStatus(s.id, 'dismissed')}>
                                  <XCircle className="h-4 w-4" />
                                  <span>Dismiss</span>
                                </DropdownMenuItem>
                              )}
                              {s.status !== 'open' && (
                                <DropdownMenuItem onClick={() => updateStatus(s.id, 'open')}>
                                  <AlertCircle className="h-4 w-4" />
                                  <span>Reopen</span>
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>

                      {/* Expanded detail row */}
                      {isExpanded && (s.detail || s.source_quote) && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={5} className="pt-0 pb-4 pl-12">
                            <div className="space-y-2">
                              {s.detail && (
                                <p className="text-sm text-muted-foreground">{s.detail}</p>
                              )}
                              {s.source_quote && (
                                <blockquote className="border-l-2 border-muted-foreground/30 pl-3 text-sm italic text-muted-foreground/80">
                                  "{s.source_quote}"
                                </blockquote>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageStart = Math.max(1, Math.min(currentPage - 2, totalPages - 4))
                const page = pageStart + i
                if (page > totalPages) return null
                return (
                  <Button
                    key={page}
                    variant={page === currentPage ? 'default' : 'ghost'}
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Button>
                )
              }).filter(Boolean)}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SuggestionsTab
